import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faRocket, faEdit, faEye, faHeart,
  faPhone, faEnvelope, faGlobe, faMapMarkerAlt,
  faStar, faCalendarAlt, faCrown, faGem, faFire,
  faBullhorn, faArrowUp, faTrophy, faCheckCircle,
  faExclamationTriangle, faLightbulb, faClock,
  faUsers, faMousePointer, faPercentage, faCoins,
  faMapMarkedAlt,
  faArrowRight, faSync
} from '@fortawesome/free-solid-svg-icons';
import { firestore } from '../FirebaseConfig';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit as fsLimit } from 'firebase/firestore';
import { BRAND } from '../styles/colors';
import '../styles/BusinessOwnerFeatures.css';

/**
 * Analytics Card Component - Shows key metrics for a listing
 */
export const AnalyticsCard = ({ 
  title, 
  value, 
  change, 
  changeType = 'neutral',
  icon,
  description,
  loading = false,
  onClick
}) => {
  if (loading) {
    return (
      <div className="analytics-card analytics-card--loading">
        <div className="analytics-card__skeleton-icon" />
        <div className="analytics-card__skeleton-content">
          <div className="analytics-card__skeleton-title" />
          <div className="analytics-card__skeleton-value" />
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`analytics-card ${onClick ? 'analytics-card--clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="analytics-card__icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="analytics-card__content">
        <span className="analytics-card__title">{title}</span>
        <span className="analytics-card__value">{value}</span>
        {change !== undefined && (
          <span className={`analytics-card__change analytics-card__change--${changeType}`}>
            {changeType === 'positive' && '+'}
            {change}
            {description && <span className="analytics-card__description">{description}</span>}
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Owner Analytics Dashboard - Comprehensive metrics view
 */
export const OwnerDashboard = ({ 
  listingId, 
  ownerId,
  dateRange = '30d',
  onDateRangeChange 
}) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(dateRange);
  const [eventRows, setEventRows] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!listingId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch analytics from Firestore
        const analyticsRef = doc(firestore, 'listingAnalytics', listingId);
        const analyticsSnap = await getDoc(analyticsRef);
        
        if (analyticsSnap.exists()) {
          setAnalytics(analyticsSnap.data());
        } else {
          // Initialize with default analytics
          setAnalytics({
            views: 0,
            viewsChange: 0,
            impressions: 0,
            impressionsChange: 0,
            favorites: 0,
            favoritesChange: 0,
            calls: 0,
            callsChange: 0,
            emails: 0,
            emailsChange: 0,
            websiteClicks: 0,
            websiteClicksChange: 0,
            directionsClicks: 0,
            directionsClicksChange: 0,
            conversionRate: 0,
            avgTimeOnPage: 0,
            topReferrers: [],
            peakHours: []
          });
        }
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [listingId, selectedPeriod]);

  // Fetch per-event analytics rollups for events tied to this listing.
  useEffect(() => {
    if (!listingId) return;
    let cancelled = false;
    (async () => {
      setEventsLoading(true);
      try {
        const eventsSnap = await getDocs(
          query(
            collection(firestore, 'events'),
            where('businessId', '==', listingId),
            orderBy('startAt', 'desc'),
            fsLimit(50),
          ),
        );
        const evs = eventsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const withAnalytics = await Promise.all(
          evs.map(async (ev) => {
            try {
              const aSnap = await getDoc(doc(firestore, 'eventAnalytics', ev.id));
              return { ...ev, analytics: aSnap.exists() ? aSnap.data() : {} };
            } catch {
              return { ...ev, analytics: {} };
            }
          }),
        );
        if (!cancelled) setEventRows(withAnalytics);
      } catch (err) {
        if (!cancelled) setEventRows([]);
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [listingId]);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    onDateRangeChange?.(period);
  };

  const getChangeType = (value) => {
    if (value > 0) return 'positive';
    if (value < 0) return 'negative';
    return 'neutral';
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  return (
    <div className="owner-dashboard">
      <div className="owner-dashboard__header">
        <h2 className="owner-dashboard__title">
          <FontAwesomeIcon icon={faChartLine} />
          Listing Analytics
        </h2>
        <div className="owner-dashboard__period-selector">
          {['7d', '30d', '90d', '1y'].map((period) => (
            <button
              key={period}
              className={`owner-dashboard__period-btn ${selectedPeriod === period ? 'active' : ''}`}
              onClick={() => handlePeriodChange(period)}
            >
              {period === '7d' ? '7 Days' : 
               period === '30d' ? '30 Days' : 
               period === '90d' ? '90 Days' : '1 Year'}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="owner-dashboard__error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          {error}
        </div>
      )}

      <div className="owner-dashboard__metrics">
        <AnalyticsCard
          title="Page Views"
          value={formatNumber(analytics?.views || 0)}
          change={`${analytics?.viewsChange || 0}%`}
          changeType={getChangeType(analytics?.viewsChange)}
          icon={faEye}
          description="vs previous period"
          loading={loading}
        />
        <AnalyticsCard
          title="Impressions"
          value={formatNumber(analytics?.impressions || 0)}
          change={`${analytics?.impressionsChange || 0}%`}
          changeType={getChangeType(analytics?.impressionsChange)}
          icon={faMousePointer}
          description="search appearances"
          loading={loading}
        />
        <AnalyticsCard
          title="Map Impressions"
          value={formatNumber(analytics?.mapImpressions || 0)}
          icon={faMapMarkedAlt}
          description="promotional map placements"
          loading={loading}
        />
        <AnalyticsCard
          title="Map Clicks"
          value={formatNumber(analytics?.mapClicks || 0)}
          icon={faMapMarkedAlt}
          description="clicks from map placements"
          loading={loading}
        />
        <AnalyticsCard
          title="Favorites"
          value={formatNumber(analytics?.favorites || 0)}
          change={`${analytics?.favoritesChange || 0}%`}
          changeType={getChangeType(analytics?.favoritesChange)}
          icon={faHeart}
          description="saved by users"
          loading={loading}
        />
        <AnalyticsCard
          title="Phone Calls"
          value={formatNumber(analytics?.calls || 0)}
          change={`${analytics?.callsChange || 0}%`}
          changeType={getChangeType(analytics?.callsChange)}
          icon={faPhone}
          description="click to call"
          loading={loading}
        />
        <AnalyticsCard
          title="Email Inquiries"
          value={formatNumber(analytics?.emails || 0)}
          change={`${analytics?.emailsChange || 0}%`}
          changeType={getChangeType(analytics?.emailsChange)}
          icon={faEnvelope}
          loading={loading}
        />
        <AnalyticsCard
          title="Website Clicks"
          value={formatNumber(analytics?.websiteClicks || 0)}
          change={`${analytics?.websiteClicksChange || 0}%`}
          changeType={getChangeType(analytics?.websiteClicksChange)}
          icon={faGlobe}
          loading={loading}
        />
        <AnalyticsCard
          title="Directions"
          value={formatNumber(analytics?.directionsClicks || 0)}
          change={`${analytics?.directionsClicksChange || 0}%`}
          changeType={getChangeType(analytics?.directionsClicksChange)}
          icon={faMapMarkerAlt}
          loading={loading}
        />
        <AnalyticsCard
          title="Conversion Rate"
          value={`${(analytics?.conversionRate || 0).toFixed(1)}%`}
          icon={faPercentage}
          description="views to actions"
          loading={loading}
        />
      </div>

      <div className="owner-dashboard__insights">
        <div className="owner-dashboard__insight-card">
          <div className="insight-card__header">
            <FontAwesomeIcon icon={faClock} />
            <h3>Peak Hours</h3>
          </div>
          <div className="insight-card__content">
            {analytics?.peakHours?.length > 0 ? (
              <div className="peak-hours-chart">
                {analytics.peakHours.map((hour, index) => (
                  <div 
                    key={hour.hour}
                    className="peak-hour-bar"
                    style={{ height: `${hour.percentage}%` }}
                    title={`${hour.hour}:00 - ${hour.views} views`}
                  >
                    <span className="peak-hour-label">{hour.hour}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="insight-card__empty">No data available yet</p>
            )}
          </div>
        </div>

        <div className="owner-dashboard__insight-card">
          <div className="insight-card__header">
            <FontAwesomeIcon icon={faUsers} />
            <h3>Top Referrers</h3>
          </div>
          <div className="insight-card__content">
            {analytics?.topReferrers?.length > 0 ? (
              <ul className="referrers-list">
                {analytics.topReferrers.map((referrer, index) => (
                  <li key={referrer.source} className="referrer-item">
                    <span className="referrer-source">{referrer.source}</span>
                    <span className="referrer-count">{referrer.visits} visits</span>
                    <div 
                      className="referrer-bar" 
                      style={{ width: `${referrer.percentage}%` }}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="insight-card__empty">No referrer data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Events analytics — per-event rollups + aggregate row */}
      <div className="owner-dashboard__events">
        <h3 className="owner-dashboard__events-title">
          <FontAwesomeIcon icon={faCalendarAlt} /> Events Performance
        </h3>
        {eventsLoading ? (
          <p className="insight-card__empty">Loading event metrics…</p>
        ) : eventRows.length === 0 ? (
          <p className="insight-card__empty">
            Create an event to start tracking attendance and engagement.
          </p>
        ) : (
          <>
            {(() => {
              const totals = eventRows.reduce(
                (acc, ev) => ({
                  views: acc.views + (ev.analytics?.views || 0),
                  rsvps: acc.rsvps + (ev.rsvpCount || 0),
                  shares: acc.shares + (ev.analytics?.shareClicks || 0),
                  directions: acc.directions + (ev.analytics?.directionsClicks || 0),
                }),
                { views: 0, rsvps: 0, shares: 0, directions: 0 },
              );
              return (
                <div className="owner-dashboard__events-totals">
                  <span><FontAwesomeIcon icon={faEye} /> {totals.views} total views</span>
                  <span><FontAwesomeIcon icon={faUsers} /> {totals.rsvps} total RSVPs</span>
                  <span><FontAwesomeIcon icon={faBullhorn} /> {totals.shares} shares</span>
                  <span><FontAwesomeIcon icon={faMapMarkerAlt} /> {totals.directions} directions</span>
                </div>
              );
            })()}
            <div className="owner-dashboard__events-table-wrap">
              <table className="owner-dashboard__events-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Date</th>
                    <th>Views</th>
                    <th>RSVPs</th>
                    <th>Shares</th>
                    <th>Directions</th>
                  </tr>
                </thead>
                <tbody>
                  {eventRows.map((ev) => {
                    const ts = ev.nextStartAt
                      || (ev.startAt && (ev.startAt.toMillis ? ev.startAt.toMillis() : new Date(ev.startAt).getTime()))
                      || null;
                    const dateLabel = ts
                      ? new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                      : '—';
                    return (
                      <tr key={ev.id}>
                        <td>{ev.title}</td>
                        <td>{dateLabel}</td>
                        <td>{ev.analytics?.views || 0}</td>
                        <td>
                          {ev.rsvpCount || 0}
                          {ev.capacity ? ` / ${ev.capacity}` : ''}
                        </td>
                        <td>{ev.analytics?.shareClicks || 0}</td>
                        <td>{ev.analytics?.directionsClicks || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Listing Promotion Component - Boost visibility options
 */
export const ListingPromotion = ({ 
  listing, 
  userTokens = 0,
  onPromote,
  onUpgrade 
}) => {
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const promotionOptions = [
    {
      id: 'boost',
      name: 'Quick Boost',
      icon: faRocket,
      duration: '24 hours',
      tokenCost: 10,
      benefits: [
        'Your listing rises to the top of search results for 24 hours',
        'Perfect for events, specials, or weekend promos',
        'Instant activation — no waiting'
      ],
      color: BRAND.navy
    },
    {
      id: 'spotlight',
      name: 'Spotlight',
      icon: faFire,
      duration: '3 days',
      tokenCost: 30,
      benefits: [
        'Your business rotates through "Tonight\'s Featured" on the homepage',
        'Seen by every visitor to the Guild for 3 full days',
        'Highlighted card in all browse results'
      ],
      color: BRAND.oxblood
    },
    {
      id: 'featured',
      name: 'Featured Listing',
      icon: faStar,
      duration: '7 days',
      tokenCost: 50,
      benefits: [
        'Leads the Featured Businesses grid on the homepage for a full week',
        '★ Featured badge on your listing card across all browse results',
        'First position in your category search results'
      ],
      color: BRAND.brass
    },
    {
      id: 'premium',
      name: 'Premium Placement',
      icon: faCrown,
      duration: '30 days',
      tokenCost: 150,
      benefits: [
        'Top-pinned in your category for 30 days — never buried',
        'Homepage featured grid rotation with "⚓ Captain\'s Pick" badge',
        'Priority placement in every search and filter result',
        'Maximum long-term visibility for established businesses'
      ],
      color: BRAND.oxbloodDark
    }
  ];

  const handleSelectPromotion = (promo) => {
    setSelectedPromotion(selectedPromotion?.id === promo.id ? null : promo);
  };

  const handleApplyPromotion = async () => {
    if (!selectedPromotion) return;
    
    if (userTokens < selectedPromotion.tokenCost) {
      onUpgrade?.();
      return;
    }

    setIsProcessing(true);
    try {
      await onPromote?.(selectedPromotion);
      setSelectedPromotion(null);
    } catch (error) {
      console.error('Promotion error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const currentPromotion = listing?.promotion;
  const isAlreadyPromoted = currentPromotion?.isActive && currentPromotion?.expiresAt && new Date(currentPromotion.expiresAt) > new Date();

  const promotionExpiresOn = isAlreadyPromoted
    ? new Date(currentPromotion.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  const daysRemaining = isAlreadyPromoted
    ? Math.max(0, Math.ceil((new Date(currentPromotion.expiresAt) - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div className="listing-promotion">
      <div className="listing-promotion__header">
        <h2>
          <FontAwesomeIcon icon={faBullhorn} />
          Promote Your Listing
        </h2>
        <div className="listing-promotion__tokens">
          <FontAwesomeIcon icon={faCoins} />
          <span>{userTokens} tokens available</span>
        </div>
      </div>

      {/* Active promotion success card */}
      {isAlreadyPromoted && (
        <div className="listing-promotion__active-card">
          <div className="active-promo-icon">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="active-promo-body">
            <strong>Your promotion is live!</strong>
            <p>
              <span className="active-promo-type">{currentPromotion.type?.charAt(0).toUpperCase() + currentPromotion.type?.slice(1)}</span> promotion
              is active until <strong>{promotionExpiresOn}</strong>
              {daysRemaining > 0 && ` (${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining)`}.
            </p>
            <p className="active-promo-hint">
              Your listing is currently featured across the Guild. You can apply a new promotion once this one expires.
            </p>
          </div>
        </div>
      )}

      {/* How promotions work */}
      {!isAlreadyPromoted && (
        <div className="listing-promotion__info-callout">
          <FontAwesomeIcon icon={faLightbulb} />
          <div>
            <strong>How promotions work</strong>
            <p>
              Choose a tier below and spend tokens to promote your listing. Promoted listings appear
              in the <strong>"Tonight's Featured"</strong> carousel on the homepage, lead the Featured
              Businesses grid, and rise to the top of category searches — so travellers find you first.
              {' '}<a href="/tokens" className="promo-callout-link">Need more tokens?</a>
            </p>
            <p className="promo-callout-note">
              Promotions are non-refundable and cannot be stacked. Durations start immediately upon purchase.
            </p>
          </div>
        </div>
      )}

      <div className="listing-promotion__options">
        {promotionOptions.map((promo) => (
          <div
            key={promo.id}
            className={`promotion-option ${selectedPromotion?.id === promo.id ? 'selected' : ''} ${userTokens < promo.tokenCost ? 'insufficient' : ''}`}
            onClick={() => handleSelectPromotion(promo)}
            style={{ '--promo-color': promo.color }}
          >
            <div className="promotion-option__icon">
              <FontAwesomeIcon icon={promo.icon} />
            </div>
            <div className="promotion-option__content">
              <h3 className="promotion-option__name">{promo.name}</h3>
              <span className="promotion-option__duration">{promo.duration}</span>
              <ul className="promotion-option__benefits">
                {promo.benefits.map((benefit, index) => (
                  <li key={index}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <div className="promotion-option__cost">
              <FontAwesomeIcon icon={faCoins} />
              <span>{promo.tokenCost}</span>
            </div>
          </div>
        ))}
      </div>

      {selectedPromotion && (
        <div className="listing-promotion__action">
          {userTokens >= selectedPromotion.tokenCost ? (
            <button 
              className="promotion-apply-btn"
              onClick={handleApplyPromotion}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <FontAwesomeIcon icon={faSync} spin />
                  Processing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faArrowUp} />
                  Apply {selectedPromotion.name}
                </>
              )}
            </button>
          ) : (
            <button 
              className="promotion-upgrade-btn"
              onClick={onUpgrade}
            >
              <FontAwesomeIcon icon={faGem} />
              Get More Tokens
            </button>
          )}
        </div>
      )}

      <div className="listing-promotion__tips">
        <FontAwesomeIcon icon={faLightbulb} />
        <p>
          <strong>Pro Tip:</strong> Combine promotions with updates to your listing 
          (new photos, updated hours) for maximum impact!
        </p>
      </div>
    </div>
  );
};

/**
 * Owner Actions Component - Quick actions for listing management
 */
export const OwnerActions = ({ 
  listing,
  onEdit,
  onPromote,
  onViewAnalytics,
  onDuplicate,
  onArchive,
  onDelete
}) => {
  const [showConfirm, setShowConfirm] = useState(null);

  const actions = [
    {
      id: 'edit',
      label: 'Edit Listing',
      icon: faEdit,
      onClick: onEdit,
      variant: 'primary'
    },
    {
      id: 'analytics',
      label: 'View Analytics',
      icon: faChartLine,
      onClick: onViewAnalytics,
      variant: 'secondary'
    },
    {
      id: 'promote',
      label: 'Promote',
      icon: faRocket,
      onClick: onPromote,
      variant: 'accent',
      badge: 'Boost'
    }
  ];

  const moreActions = [
    {
      id: 'duplicate',
      label: 'Duplicate Listing',
      icon: faSync,
      onClick: onDuplicate
    },
    {
      id: 'archive',
      label: 'Archive Listing',
      icon: faCalendarAlt,
      onClick: onArchive,
      confirm: 'Are you sure you want to archive this listing?'
    }
  ];

  const handleAction = (action) => {
    if (action.confirm) {
      setShowConfirm(action);
    } else {
      action.onClick?.();
    }
  };

  const confirmAction = () => {
    showConfirm?.onClick?.();
    setShowConfirm(null);
  };

  return (
    <div className="owner-actions">
      <div className="owner-actions__primary">
        {actions.map((action) => (
          <button
            key={action.id}
            className={`owner-action-btn owner-action-btn--${action.variant}`}
            onClick={() => handleAction(action)}
          >
            <FontAwesomeIcon icon={action.icon} />
            <span>{action.label}</span>
            {action.badge && (
              <span className="owner-action-btn__badge">{action.badge}</span>
            )}
          </button>
        ))}
      </div>

      <div className="owner-actions__secondary">
        {moreActions.map((action) => (
          <button
            key={action.id}
            className="owner-action-link"
            onClick={() => handleAction(action)}
          >
            <FontAwesomeIcon icon={action.icon} />
            <span>{action.label}</span>
          </button>
        ))}
      </div>

      {showConfirm && (
        <div className="owner-actions__confirm-overlay">
          <div className="owner-actions__confirm-dialog">
            <p>{showConfirm.confirm}</p>
            <div className="confirm-dialog__actions">
              <button 
                className="confirm-dialog__cancel"
                onClick={() => setShowConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="confirm-dialog__confirm"
                onClick={confirmAction}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Listing Performance Summary - Compact widget for listing cards
 */
export const PerformanceSummary = ({ listingId, compact = false }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!listingId) return;
      
      try {
        const analyticsRef = doc(firestore, 'listingAnalytics', listingId);
        const snap = await getDoc(analyticsRef);
        
        if (snap.exists()) {
          setStats(snap.data());
        }
      } catch (err) {
        console.error('Error fetching performance:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [listingId]);

  if (loading) {
    return (
      <div className={`performance-summary ${compact ? 'performance-summary--compact' : ''}`}>
        <div className="performance-summary__skeleton" />
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className={`performance-summary ${compact ? 'performance-summary--compact' : ''}`}>
      <div className="performance-summary__stat">
        <FontAwesomeIcon icon={faEye} />
        <span>{stats.views || 0}</span>
      </div>
      <div className="performance-summary__stat">
        <FontAwesomeIcon icon={faHeart} />
        <span>{stats.favorites || 0}</span>
      </div>
      <div className="performance-summary__stat">
        <FontAwesomeIcon icon={faPhone} />
        <span>{stats.calls || 0}</span>
      </div>
      {!compact && stats.conversionRate > 0 && (
        <div className="performance-summary__stat performance-summary__stat--highlight">
          <FontAwesomeIcon icon={faPercentage} />
          <span>{stats.conversionRate.toFixed(1)}%</span>
        </div>
      )}
    </div>
  );
};

/**
 * Listing Tips Card - Suggestions for improving listing performance
 */
export const ListingTips = ({ listing }) => {
  const tips = useMemo(() => {
    const suggestions = [];

    // Check for missing or improvable elements
    if (!listing?.images || listing.images.length < 3) {
      suggestions.push({
        icon: faLightbulb,
        priority: 'high',
        title: 'Add More Photos',
        description: 'Listings with 3+ photos get 2x more views',
        action: 'Add Photos'
      });
    }

    if (!listing?.hours || Object.keys(listing.hours).length === 0) {
      suggestions.push({
        icon: faClock,
        priority: 'medium',
        title: 'Add Business Hours',
        description: 'Help customers know when you\'re open',
        action: 'Set Hours'
      });
    }

    if (!listing?.description || listing.description.length < 100) {
      suggestions.push({
        icon: faEdit,
        priority: 'medium',
        title: 'Expand Your Description',
        description: 'Detailed descriptions rank higher in search',
        action: 'Edit Description'
      });
    }

    if (!listing?.website) {
      suggestions.push({
        icon: faGlobe,
        priority: 'low',
        title: 'Add Your Website',
        description: 'Drive more traffic to your site',
        action: 'Add Website'
      });
    }

    return suggestions;
  }, [listing]);

  if (tips.length === 0) {
    return (
      <div className="listing-tips listing-tips--complete">
        <FontAwesomeIcon icon={faTrophy} />
        <h3>Great job!</h3>
        <p>Your listing is fully optimized</p>
      </div>
    );
  }

  return (
    <div className="listing-tips">
      <h3 className="listing-tips__title">
        <FontAwesomeIcon icon={faLightbulb} />
        Improve Your Listing
      </h3>
      <ul className="listing-tips__list">
        {tips.map((tip, index) => (
          <li key={index} className={`listing-tip listing-tip--${tip.priority}`}>
            <FontAwesomeIcon icon={tip.icon} className="listing-tip__icon" />
            <div className="listing-tip__content">
              <strong>{tip.title}</strong>
              <p>{tip.description}</p>
            </div>
            <button className="listing-tip__action">
              {tip.action}
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Export all components
const BusinessOwnerFeaturesExports = {
  AnalyticsCard,
  OwnerDashboard,
  ListingPromotion,
  OwnerActions,
  PerformanceSummary,
  ListingTips
};

export default BusinessOwnerFeaturesExports;
